import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  startAfter,
  limit,
  getCountFromServer,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import moment from "moment-timezone"; // Import moment-timezone for timezone handling

const useTableFunction = ({
  collectionPath,
  defaultOrderBy,
  dynamicCondition,
  conditionOrderBy,
}) => {
  const [tableData, setTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalLength, setTotalLength] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [searchData, setSearchData] = useState({ column: "", query: "" });
  const [searchDateRange, setSearchDateRange] = useState({
    fromDate: "",
    toDate: "",
    column: "",
  });
  const [orderByField, setOrderByField] = useState({
    column: "",
    direction: "",
  });
  const [lastDocument, setLastDocument] = useState(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchData, pageSize, orderByField, searchDateRange]);

  const fetchData = async () => {
    try {
      setIsTableLoading(true);

      const collectionRef = collection(db, collectionPath);

      let baseQuery = defaultOrderBy
        ? query(collectionRef, orderBy(defaultOrderBy))
        : collectionRef;

      if (orderByField.column && orderByField.direction) {
        baseQuery = query(
          collectionRef,
          orderBy(orderByField.column, orderByField.direction)
        );
      }
      if (searchData.column && searchData.query) {
        if (searchData.column === "id") {
          console.log("main")
          const docRef = doc(db, collectionPath, searchData.query);
          const docSnap = await getDoc(docRef);
          const docData = { ...docSnap.data(), id: docSnap.id };
          setTableData([docData]); // Set the table data with the document data
          setIsTableLoading(false);
          return;

        }
        if (dynamicCondition && searchData.column) {
          const collectionRef = collection(db, collectionPath);
        
          // Construct the dynamic query
          let filters = [where(searchData.column, "==", searchData.query)];
        
          // If there is an additional dynamic condition, apply it
          const condition = await dynamicCondition();
          if (condition && condition.column) {
            filters.push(where(condition.column, condition.operator, condition.query));
          }
        
          // Build the Firestore query with filters
          let q = query(collectionRef, ...filters, limit(pageSize));
        
          // Apply pagination only if there's a last document
          if (currentPage > 0 && lastDocument) {
            q = query(q, startAfter(lastDocument), ...filters, limit(pageSize));
          }
        
          // Fetch the data
          const querySnapshot = await getDocs(q);
          const docDataArray = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        
          console.log("Dynamic query result:", docDataArray);
        
          setTableData(docDataArray);
        
          // Update last document for pagination
          setLastDocument(querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null);
        
          // Fetch total count for dynamic query
          const countQuery = query(collectionRef, ...filters);
          const countSnapshot = await getCountFromServer(countQuery);
          setTotalLength(countSnapshot.data().count); // ✅ Update total count dynamically
        
          setIsTableLoading(false);
          return;
        }        
        
        baseQuery = query(
          collectionRef,
          orderBy(searchData.column),
          where(searchData.column, ">=", searchData.query),
          where(searchData.column, "<=", searchData.query + "\uf8ff")
        );
        
      }

      if (dynamicCondition) {
        const condition = await dynamicCondition();
        if (condition.column === "id") {
          const docRef = doc(db, collectionPath, condition.query);
          const docSnap = await getDoc(docRef);
          const docData = { ...docSnap.data(), id: docSnap.id };
          setTableData([docData]); // Set the table data with the document data
          setIsTableLoading(false);
          return;
        } else {
          baseQuery = query(
            collectionRef,
            where(condition.column, condition.operator, condition.query)
          );
        }
      }

      if (conditionOrderBy) {
        baseQuery = query(baseQuery, orderBy(conditionOrderBy));
      }

      // Apply date range filtering with IST timezone handling
      if (
        searchDateRange.fromDate &&
        searchDateRange.toDate &&
        searchDateRange.column
      ) {
        // Convert 'fromDate' to IST 12:00 AM
        const fromDate = moment.tz(searchDateRange.fromDate, "Asia/Kolkata").startOf("day").toDate();
        
        // Convert 'toDate' to IST 11:59:59.999 PM
        const toDate = moment.tz(searchDateRange.toDate, "Asia/Kolkata").endOf("day").toDate();

        // Log the date range for debugging
        console.log("Date Range:");
        console.log("From Date (12:00 AM IST):", fromDate);
        console.log("To Date (11:59:59.999 PM IST):", toDate);

        if (dynamicCondition) {
          const condition = await dynamicCondition();
          baseQuery = query(
            collectionRef,
            orderBy(searchDateRange.column),
            where(condition.column, condition.operator, condition.query),
            where(searchDateRange.column, ">=", fromDate),
            where(searchDateRange.column, "<=", toDate)
          );
        } else {
          baseQuery = query(
            collectionRef,
            orderBy(searchDateRange.column),
            where(searchDateRange.column, ">=", fromDate),
            where(searchDateRange.column, "<=", toDate)
          );
        }
      }

      const countSnapshot = await getCountFromServer(baseQuery);
      setTotalLength(countSnapshot.data().count);

      let paginatedQuery = query(
        baseQuery,
        currentPage > 0 ? startAfter(lastDocument) : undefined,
        limit(pageSize)
      );

      const dataSnapshot = await getDocs(paginatedQuery);

      setLastDocument(dataSnapshot.docs[dataSnapshot.docs.length - 1]);
      const paginatedData = dataSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      setTableData(paginatedData);
      setIsTableLoading(false);
    } catch (error) {
      setIsTableLoading(false);
      console.log("Error ", error);
    }
  };

  const refetchTableData = () => {
    fetchData();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchQuery = (column = "", query = "") => {
    if (
      searchData.query !== query ||
      (searchData.query && searchData.column !== column)
    ) {
      setSearchData({ column, query });
    }
    console.log(column,query)

  };

  const handlePageSize = (pageSize) => {
    setCurrentPage(0);
    setPageSize(pageSize);
  };

  const handleTableSorting = (column, direction) => {
    setOrderByField({ column, direction });
  };

  const handleSearchDate = (fromDate, toDate, searchColumn) => {
    setCurrentPage(0);
    setSearchDateRange({
      fromDate,
      toDate,
      column: searchColumn,
    });
  };

  return {
    tableData,
    isTableLoading,
    currentPage,
    totalLength,
    handleSearchQuery,
    handlePageChange,
    handlePageSize,
    handleTableSorting,
    handleSearchDate,
    refetchTableData,
  };
};

export default useTableFunction;


  // Assuming collectionRef is already defined and points to a Firestore collection
// const baseQuery = query(
//   collectionRef,
//   where(condition.column, condition.operator, condition.query),
//   where(searchData.column, "==", searchData.query)
// );

// Fetch the query results
//const querySnapshot =  getDocs(baseQuery);

// Map over the snapshot to get an array of document data
//const allData = querySnapshot.docs.map(doc => doc.data());

//console.log(allData);  // This will contain all the documents that match your query